<template>
  <flex-column class="group-edit-modal">
    <flex-header>
      <div class="modal-header">
        <flex-row gap vcenter>
          <flex-cell>
            <div class="header-eyebrow" v-if="options.eyebrow">
              {{ options.eyebrow }}
            </div>
            <h3>{{ heading }}</h3>
          </flex-cell>
          <flex-cell shrink>
            <ux-button icon @click="dismiss">
              <ux-icon icon="fa-times" />
            </ux-button>
          </flex-cell>
        </flex-row>
      </div>
    </flex-header>
    <flex-body>
      <div class="modal-body">
        <ux-form
          :fields="options.fields"
          :flex="options.sameLine"
          v-model="bound"
          @form:state="onFormState"
        />

        <div v-if="invalidFields.length" class="invalid-field-message">
          <h5>Please fix the issues below to continue</h5>
          <div v-for="field in invalidFields" :key="field.key">
            {{
              field.message ||
              `Please provide a valid answer for ${field.title}`
            }}
          </div>
        </div>
      </div>
    </flex-body>
    <flex-footer>
      <div class="modal-footer">
        <flex-row>
          <flex-spacer />
          <flex-cell shrink>
            <span :tooltip="tooltip">
              <ux-button color="primary" :disabled="isInvalid" @click="confirm"
                >Done</ux-button
              >
            </span>
          </flex-cell>
        </flex-row>
      </div>
    </flex-footer>
  </flex-column>
</template>
<script>
import ModalMixin from "./ModalMixin";

export default {
  mixins: [ModalMixin],
  data() {
    return {
      formState: null,
      attempted: false,
    };
  },
  computed: {
    heading() {
      return this.options.title || "Edit";
    },
    bound: {
      get() {
        if (this.options.array) {
          return this.options.array[this.options.index];
        }
        return this.options.model;
      },
      set(value) {
        if (this.options.array) {
          // Never write null entries, and never write past the end of the
          // array (a stale index creates holes that persist as null in JSON)
          if (value == null) {
            return;
          }
          if (
            this.options.index < 0 ||
            this.options.index >= this.options.array.length
          ) {
            return;
          }
          this.options.array[this.options.index] = value;
        } else if (typeof this.options.onUpdate === "function") {
          this.options.onUpdate(value);
        }
      },
    },
    isInvalid() {
      return !!this.formState?.invalid;
    },
    invalidFields() {
      if (!this.attempted || !this.isInvalid) return [];
      return this.formState?.invalidFields || [];
    },
    tooltip() {
      return this.isInvalid ? "Please check the errors in the form" : undefined;
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  },
  methods: {
    onFormState(state) {
      this.formState = state;
      if (typeof this.options.onStateChange === "function") {
        this.options.onStateChange(state);
      }
    },
    confirm() {
      if (this.isInvalid) {
        this.attempted = true;
        return;
      }
      this.dismiss();
    },
    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.dismiss();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.group-edit-modal {
  border-radius: 0.5em;
  background: var(--bg1, #fff);
  width: clamp(320px, 70vw, 720px);
  max-height: 85vh;
  overflow: hidden;
}

.modal-header {
  padding: 1em 1.25em;
  border-bottom: 1px solid color-mix(in lab, currentColor 10%, transparent);

  .header-eyebrow {
    font-size: 0.7em;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    opacity: 0.5;
    margin-bottom: 4px;
  }

  h3 {
    margin: 0;
    font-size: 1.1em;
    font-weight: 600;
    line-height: 1.2;
  }
}

.modal-body {
  padding: 1.25em 1.5em;
}

.modal-footer {
  padding: 0.75em 1em;
  border-top: 1px solid color-mix(in lab, currentColor 10%, transparent);
}

.invalid-field-message {
  margin-top: 1em;
  padding: 0.85em 1em;
  border-radius: 8px;
  background: color-mix(in lab, var(--red, #df7d7d) 8%, transparent);
  border: 1px solid color-mix(in lab, var(--red, #df7d7d) 25%, transparent);
  color: var(--red, #df7d7d);
  font-size: 0.9em;

  h5 {
    margin: 0 0 0.4em;
    font-size: 1em;
    font-weight: 600;
  }

  div {
    margin-top: 0.2em;
  }
}
</style>
